import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  EntityIdentifier,
  getObjectId,
  Meter,
  MeterEntity,
  meterSelector,
  useCreateMeterMutation,
  useGetMeterQuery,
  useUpdateMeterMutation
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {notifications} from "@mantine/notifications";
import {MeterForm, MeterFormValues} from "../../components/form/MeterForm/MeterForm";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: Partial<MeterFormValues>
}

export const MeterFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  return <MeterRequestFormPage id={id} copy={copy} formDefaults={formDefaults}/>;
};

export const toFormDefaultsFromMeter = (meter: MeterEntity | undefined): Partial<MeterFormValues> => {
  let object: EntityIdentifier | undefined = undefined

  if (meter && meter.objectId && meter.objectType) {
    object = {
      id: meter.objectId,
      type: meter.objectType,
    }
  }

  return {
    name: meter?.name,
    type: meter?.type,
    flowMeterType: meter?.flowMeterType,
    barcode: meter?.barcode,
    unitOfMeasure: meter?.unitOfMeasure,
    freightType: meter?.freightTypeId,
    object: object,
    doNotUse: meter?.doNotUse,
  };
};

export const MeterRequestFormPage = (props: Props) => {
  const {id, copy = false, formDefaults} = props;

  const navigate = useNavigate();
  const {regionId} = useApp()
  const {t} = useTranslation(["translation", "common"]);

  const [access, setAccess] = useState(false);

  const selectMeter = useMemo(() => {
    return meterSelector()
  }, [])

  const {currMeter, isLoading} = useGetMeterQuery(access ? (id ?? skipToken) : skipToken, {
    selectFromResult: (result) => ({
      currMeter: selectMeter(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: Partial<MeterFormValues> = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaultsFromMeter(currMeter);
  }, [currMeter, formDefaults]);

  const isEdit = useMemo(() => {
    return currMeter && !copy
  }, [currMeter, copy])

  const titleKey: string = useMemo(() => {
    if (isEdit) {
      return "page.meter.form.title.edit";
    } else {
      return "page.meter.form.title.new";
    }
  }, [isEdit]);

  const [createMeter, {isLoading: isCreating}] = useCreateMeterMutation();
  const [updateMeter, {isLoading: isUpdating}] = useUpdateMeterMutation();

  const onMeterSubmit = useCallback((formValues: MeterFormValues) => {
    if (isEdit) {
      const meter: Partial<Meter> = {
        ...currMeter.asPlainValue(),
        name: formValues.name,
        type: formValues.type.code,
        flowMeterType: formValues.flowMeterType?.code,
        barcode: formValues.barcode,
        unitOfMeasure: formValues.unitOfMeasure,
        freightTypeId: getObjectId(formValues.freightType),
        objectId: formValues.object?.id,
        objectType: formValues.object?.type,
        doNotUse: formValues.doNotUse,
      };
      updateMeter(meter)
        .unwrap()
        .then(() => {
          notifications.show({
            color: "green",
            title: t("common.entity.meter", "Meter", {ns: "common"}) + " " + t("common.status.updated", "Updated", {ns: "common"}),
            message: ""
          });
          navigate(-1);
        })
        .catch((rejected) =>
          notifications.show({
            color: "red",
            title: t("common.error", "Error", {ns: "common"}),
            message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"})
          })
        );
    } else {
      const meter: Partial<Meter> = {
        name: formValues.name,
        type: formValues.type.code,
        flowMeterType: formValues.flowMeterType?.code,
        barcode: formValues.barcode,
        unitOfMeasure: formValues.unitOfMeasure,
        freightTypeId: getObjectId(formValues.freightType),
        objectId: formValues.object?.id,
        objectType: formValues.object?.type,
        doNotUse: formValues.doNotUse,
        regionId: regionId,
      };
      createMeter(meter)
        .unwrap()
        .then((payload) => {
          notifications.show({
            color: "green",
            title: t("common.entity.meter", "Meter", {ns: "common"}) + " " + t("common.status.created", "Created", {ns: "common"}),
            message: ""
          });
          navigate(`/meters/profile?id=${payload.id}`, {replace: true});
        })
        .catch((rejected) =>
          notifications.show({
            color: "red",
            title: t("common.error", "Error", {ns: "common"}),
            message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"})
          })
        );
    }
  }, [currMeter, isEdit, updateMeter, t, navigate, regionId, createMeter]);

  return (
    <ProtectedPage
      permissions={[isEdit ? "meter_edit" : "meter_create"]}
      isLoading={isLoading}
      onAccess={setAccess}>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader/>}
            {!isLoading && (
              <Paper miw={1000} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {t(titleKey)}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                <MeterForm
                  defaultValues={_formDefaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onMeterSubmit}/>
              </Paper>
            )}
          </Center>
        </Container>
      )}
    </ProtectedPage>
  );
};

